import { jStat } from 'jstat';

/**
 * Calculate sample size for conversion rate tests (proportion test)
 * 
 * @param baselineConversion Baseline conversion rate as a percentage (e.g., 10 for 10%)
 * @param mde Minimum Detectable Effect as a percentage (e.g., 5 for 5%)
 * @param confidenceLevel Confidence level as a percentage (e.g., 95 for 95%)
 * @param power Statistical power as a percentage (e.g., 80 for 80%)
 * @param variants Number of variants (including control) for Bonferroni correction
 * @returns Number of samples needed per variant
 */
export const calculateConversionSampleSize = (
  baselineConversion: number,
  mde: number,
  confidenceLevel: number,
  power: number,
  variants: number = 2
): number => {
  // Convert percentages to proportions
  const p1 = baselineConversion / 100;
  const p2 = p1 * (1 + mde / 100);
  const pBar = (p1 + p2) / 2;
  
  // Calculate alpha and beta
  const alpha = (100 - confidenceLevel) / 100;
  const beta = (100 - power) / 100;
  
  // Apply Bonferroni correction for multiple comparisons when more than 2 variants
  // For n variants, we have n-1 comparisons with the control
  const comparisons = variants > 2 ? variants - 1 : 1;
  const adjustedAlpha = alpha / comparisons;
  
  // Z-scores for alpha/2 (two-tailed test) and beta
  const zAlpha = jStat.normal.inv(1 - adjustedAlpha / 2, 0, 1);
  const zBeta = jStat.normal.inv(1 - beta, 0, 1);
  
  // Sample size calculation for proportion test
  const samplesPerVariant = Math.ceil(
    ((zAlpha + zBeta) ** 2 * pBar * (1 - pBar) * 2) / ((p2 - p1) ** 2)
  );
  
  return samplesPerVariant;
};

/**
 * Calculate sample size for metric tests (t-test)
 * 
 * @param baselineValue Baseline metric value
 * @param standardDeviation Standard deviation of the metric
 * @param mde Minimum Detectable Effect as a percentage (e.g., 5 for 5%)
 * @param confidenceLevel Confidence level as a percentage (e.g., 95 for 95%)
 * @param power Statistical power as a percentage (e.g., 80 for 80%)
 * @param logTransformation Whether to apply log transformation for right-skewed data
 * @param variants Number of variants (including control) for Bonferroni correction
 * @returns Number of samples needed per variant
 */
export const calculateMetricSampleSize = (
  baselineValue: number,
  standardDeviation: number,
  mde: number,
  confidenceLevel: number,
  power: number,
  logTransformation: boolean = false,
  variants: number = 2
): number => {
  // Calculate alpha and beta
  const alpha = (100 - confidenceLevel) / 100;
  const beta = (100 - power) / 100;
  
  // Apply Bonferroni correction for multiple comparisons when more than 2 variants
  // For n variants, we have n-1 comparisons with the control
  const comparisons = variants > 2 ? variants - 1 : 1;
  const adjustedAlpha = alpha / comparisons;
  
  // Z-scores for alpha/2 (two-tailed test) and beta
  const zAlpha = jStat.normal.inv(1 - adjustedAlpha / 2, 0, 1);
  const zBeta = jStat.normal.inv(1 - beta, 0, 1);
  
  let samplesPerVariant: number;
  
  if (logTransformation) {
    // For log-normal data, we can use the delta method approximation
    // CV = standard deviation / mean
    const cv = standardDeviation / baselineValue;
    // For log-transformed data, we use a different formula based on the coefficient of variation
    const logVariance = Math.log(1 + cv * cv);
    
    // The relative effect size in log scale is approximately log(1 + mde/100)
    const logDelta = Math.log(1 + mde / 100);
    
    // Sample size calculation for log-transformed t-test
    samplesPerVariant = Math.ceil(
      2 * ((zAlpha + zBeta) ** 2) * logVariance / (logDelta ** 2)
    );
  } else {
    // Calculate the absolute difference to detect
    const delta = baselineValue * (mde / 100);
    const variance = standardDeviation ** 2;
    
    // Sample size calculation for t-test
    samplesPerVariant = Math.ceil(
      2 * ((zAlpha + zBeta) ** 2) * variance / (delta ** 2)
    );
  }
  
  return samplesPerVariant;
};

/**
 * Calculate the total sample size and days to run
 * 
 * @param samplesPerVariant Number of samples needed per variant
 * @param variants Number of variants (including control)
 * @param dailySamples Number of samples/interactions per day
 * @returns Object containing total sample size and days to run
 */
export const calculateTestDuration = (
  samplesPerVariant: number,
  variants: number,
  dailySamples: number
): { totalSampleSize: number; daysToRun: number } => {
  const totalSampleSize = samplesPerVariant * variants;
  const daysToRun = Math.ceil(totalSampleSize / dailySamples);
  
  return {
    totalSampleSize,
    daysToRun,
  };
};

/**
 * Calculate sample size based on test type and parameters
 * This is a wrapper function that calls the appropriate calculation function
 * based on the test type and returns the complete result
 * 
 * @param params Parameters for sample size calculation
 * @returns Complete calculation result
 */
export const calculateSampleSize = (params: {
  testType: 'conversion' | 'metric';
  baselineValue: number;
  standardDeviation?: number;
  mde: number;
  confidenceLevel: number;
  power: number;
  variants: number;
  dailySamples: number;
  logTransformation?: boolean;
}): { samplesPerVariant: number; totalSampleSize: number; daysToRun: number } => {
  const {
    testType,
    baselineValue,
    standardDeviation = 0,
    mde,
    confidenceLevel,
    power,
    variants,
    dailySamples,
    logTransformation = false
  } = params;
  
  let samplesPerVariant: number;
  
  if (testType === 'conversion') {
    samplesPerVariant = calculateConversionSampleSize(
      baselineValue,
      mde,
      confidenceLevel,
      power,
      variants
    );
  } else {
    samplesPerVariant = calculateMetricSampleSize(
      baselineValue,
      standardDeviation,
      mde,
      confidenceLevel,
      power,
      logTransformation,
      variants
    );
  }
  
  const { totalSampleSize, daysToRun } = calculateTestDuration(
    samplesPerVariant,
    variants,
    dailySamples
  );
  
  return {
    samplesPerVariant,
    totalSampleSize,
    daysToRun
  };
};
